import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { RuleField, RuleOperator } from "shared";
import { api } from "../../api";
import type { FieldDef } from "../../admin/fields";
import { ListEditor } from "../../admin/ListEditor";

type Rule = { id: string; stateCode: string | null; field: string; operator: string; value: string; unit: string | null; sourceId: string | null; status: string; kind: string; note: string | null };
type Row = { id: string; kind: string; year: number; stateCode: string | null; householdSize: number; amount: number; period: string; sourceId: string | null };
const opts = (xs: readonly string[]) => xs.map((x) => ({ value: x, label: x }));

const RULE_FIELDS: FieldDef[] = [
  { key: "kind", label: "Kind", type: "select", required: true,
    options: [{ value: "required", label: "Required" }, { value: "indicative", label: "Indicative" }] },
  { key: "note", label: "Note (shown to the rep; required for indicative)", type: "text", enabledWhen: { key: "kind", value: "indicative" } },
  { key: "field", label: "Field", type: "select", options: opts(RuleField.options), required: true },
  { key: "operator", label: "Operator", type: "select", options: opts(RuleOperator.options), required: true },
  { key: "value", label: "Value", type: "text", required: true },
  { key: "unit", label: "Unit", type: "text" },
  { key: "stateCode", label: "State (blank = every state)", type: "text" },
  { key: "sourceId", label: "Source id", type: "text" },
  { key: "status", label: "Status (verified needs a source)", type: "select", required: true,
    options: [{ value: "pending", label: "Pending" }, { value: "verified", label: "Verified" }] },
];
const FPL_FIELDS: FieldDef[] = [
  { key: "householdSize", label: "Household size", type: "text", required: true },
  { key: "amount", label: "Amount", type: "text", required: true },
  { key: "period", label: "Period", type: "select", required: true, options: opts(["yearly", "monthly"]) },
  { key: "sourceId", label: "Source id", type: "text" },
];

export function RulesAdmin() {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [year, setYear] = useState<number | null>(null);
  const programs = useQuery({ queryKey: ["rules-programs"], queryFn: () => api<{ programs: { slug: string; name: string }[] }>("/api/admin/rules") });
  const rules = useQuery({ queryKey: ["rules", slug], enabled: !!slug, queryFn: () => api<{ rules: Rule[] }>(`/api/admin/rules?program=${encodeURIComponent(slug)}`) });
  const tables = useQuery({ queryKey: ["income-tables"], queryFn: () => api<{ rows: Row[] }>("/api/admin/income-tables") });

  const fpl = (tables.data?.rows ?? []).filter((r) => r.kind === "fpl" && r.stateCode === null);
  const shownYear = year ?? (fpl.length ? Math.max(...fpl.map((r) => r.year)) : new Date().getFullYear());
  const fplRows = fpl.filter((r) => r.year === shownYear).sort((a, b) => a.householdSize - b.householdSize);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4">
      <section className="card">
        <h1 className="mb-1 text-lg font-semibold">Rules and income tables</h1>
        <p className="mb-3 text-sm text-slate-500">Rules the matching tool checks. A rule with no source, or whose source is not found, is saved as pending. Saving replaces the program's whole list.</p>
        <label className="label" htmlFor="rules-program">Program</label>
        <select id="rules-program" className="input" value={slug} onChange={(e) => setSlug(e.target.value)}>
          <option value="">Choose a program</option>
          {(programs.data?.programs ?? []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select>
        {(programs.error || rules.error) && <p role="alert" className="mt-2 text-sm text-red-600">{((programs.error ?? rules.error) as Error).message}</p>}
      </section>

      {slug && rules.data && (
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold">Eligibility rules</h2>
          <ListEditor key={`rules-${slug}`} kind="rules" fields={RULE_FIELDS} items={rules.data.rules}
            onSave={async (items) => {
              await api(`/api/admin/rules/${encodeURIComponent(slug)}`, { method: "PUT", body: items.map((i) => ({
                stateCode: i.stateCode ? String(i.stateCode).toUpperCase() : null, field: i.field, operator: i.operator, value: i.value,
                unit: i.unit, sourceId: i.sourceId, status: i.status, kind: i.kind, note: i.note })) });
              qc.invalidateQueries({ queryKey: ["rules"] });
            }} />
        </section>
      )}

      <section className="card">
        <h2 className="mb-1 text-sm font-semibold">Federal poverty level table</h2>
        <p className="mb-3 text-xs text-slate-500">One row per household size for the chosen year. Saving replaces that year's national rows.</p>
        <label className="label" htmlFor="fpl-year">Year</label>
        <input id="fpl-year" className="input mb-3 w-32" type="number" value={shownYear} onChange={(e) => setYear(Number(e.target.value))} />
        {tables.error && <p role="alert" className="mb-2 text-sm text-red-600">{(tables.error as Error).message}</p>}
        {tables.data && (
          <ListEditor key={`fpl-${shownYear}`} kind="income" fields={FPL_FIELDS} items={fplRows}
            onSave={async (items) => {
              await api("/api/admin/income-tables", { method: "PUT", body: { kind: "fpl", year: shownYear, stateCode: null,
                rows: items.map((i) => ({ householdSize: Number(i.householdSize), amount: Number(i.amount), period: i.period, sourceId: i.sourceId })) } });
              qc.invalidateQueries({ queryKey: ["income-tables"] });
            }} />
        )}
      </section>
    </div>
  );
}
